import { FC, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Link as MuiLink,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TablePaginationProps,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  NorthEast as NorthEastIcon,
  SouthWest as SouthWestIcon,
} from '@mui/icons-material';
import {
  useGetMerchantDepositRequestDetailsQuery,
  useLazyGetCoinsByMerchantUidQuery,
} from '../../../api/controllers/merchant';

import {
  formatAmount,
  formatBlockchainAmountWithCurrency,
  formatFiatAmountWithCurrency,
} from '../../../utils/string/string';
import { RouterPaths } from '../../../router/paths';
import { UTCToDateTime, UTCToDateTimeWithSecond } from '../../../utils';
import { InfoField, TablePagination } from '../../../components';
import { useLazyGetTransactionsQuery } from '../../../api';
import { HeadCell, Row, RowCell } from '../../../components/table/types';
import { TransactionDirection } from '../../../enums';
import { Table } from '../../../components/table/table';

export const MerchantPaymentDetails: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { request_uid } = useParams<'request_uid'>();

  // api data
  const { data: merchantDepositRequestDetails } = useGetMerchantDepositRequestDetailsQuery({
    merchant_profile_uid: profileUid ?? '',
    request_uid: request_uid ?? '',
  });
  const [getCoins, { data: coins }] = useLazyGetCoinsByMerchantUidQuery();
  const [getTransaction, { data: transactions, isFetching }] = useLazyGetTransactionsQuery();

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  useEffect(() => {
    if (merchantDepositRequestDetails?.deposit_request) {
      getCoins({
        deposit_request_uid: merchantDepositRequestDetails.deposit_request.uid,
        merchant_profile_uid: merchantDepositRequestDetails.deposit_request.merchant_profile_uid,
      });
    }
  }, [merchantDepositRequestDetails]);
  useEffect(() => {
    if (merchantDepositRequestDetails?.deposit_request) {
      getTransaction({
        params: {
          page: page + 1,
          page_size: rowsPerPage,
          merchant_deposit_request_uid: merchantDepositRequestDetails.deposit_request.uid,
        },
      });
    }
  }, [merchantDepositRequestDetails, page, rowsPerPage]);

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('merchant-payments.details.head-cells.id'),
    },
    {
      id: '2',
      label: t('merchant-payments.details.head-cells.created-at'),
    },
    {
      id: '3',
      label: t('merchant-payments.details.head-cells.status'),
    },
    {
      id: '4',
      label: t('merchant-payments.details.head-cells.direction'),
    },
    {
      id: '5',
      label: t('merchant-payments.details.head-cells.payment-method'),
    },
    {
      id: '6',
      label: t('merchant-payments.details.head-cells.blockchain'),
    },
    {
      id: '7',
      label: t('merchant-payments.details.head-cells.coin'),
    },
    {
      id: '8',
      label: t('merchant-payments.details.head-cells.amount'),
      align: 'right',
    },
    {
      id: '9',
      label: t('merchant-payments.details.head-cells.fee'),
      align: 'right',
    },
  ];

  const rows: Row[] | undefined = transactions?.transactions?.map((it) => {
    const {
      id,
      created_at,
      status,
      payment_method,
      direction,
      coin,
      amount,
      fee,
      profile_name,
      profile_uid,
      reference,
      merchant_deposit_request_uid,
    } = it;

    const cells: RowCell[] = [
      {
        name: 'id',
        value: id,
        component: (
          <MuiLink underline="hover" component={Link} to={`transactions/${id}`}>
            <Typography>{id}</Typography>
          </MuiLink>
        ),
        sx: { cursor: 'pointer' },
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTimeWithSecond(value);
        },
      },
      {
        name: 'status',
        value: status,
        formatting: (value: string) => {
          return t(`common.transaction-status.${value}`);
        },
      },
      {
        name: 'direction',
        value: direction,
        component:
          direction === TransactionDirection.OUT ? (
            <Stack direction="row" gap="4px">
              <NorthEastIcon color="error" fontSize="small" />
              {t(`common.transaction-direction.${direction}`)}
            </Stack>
          ) : (
            <Stack direction="row" gap="4px">
              <SouthWestIcon color="success" fontSize="small" />
              {t(`common.transaction-direction.${direction}`)}
            </Stack>
          ),
      },
      {
        name: 'payment_method',
        value: payment_method,
        formatting: (value: string) => {
          return merchant_deposit_request_uid
            ? `${t('transactions.merchant')} / ${t(`common.payment-method.${value}`)}`
            : t(`common.payment-method.${value}`);
        },
      },
      {
        name: 'blockchain',
        value: t(`common.blockchain.${coin.blockchain}`),
        align: 'right',
      },
      {
        name: 'coin',
        value: coin.code,
        align: 'right',
      },
      {
        name: 'amount',
        value: formatAmount(amount),
        align: 'right',
      },
      {
        name: 'fee',
        value: formatAmount(fee),
        align: 'right',
      },
    ];

    return { cells };
  });

  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" spacing={2} pl={3} pt={1}>
        <MuiLink
          underline="hover"
          component={Link}
          to={`${RouterPaths.Profiles}/${profileUid}/merchant-payments`}
        >
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('merchant-payments.details.back-to-merchant-payments')}
            </Typography>
          </Stack>
        </MuiLink>
      </Stack>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('merchant-payments.details.title')}
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('merchant-payments.details.head-cells.uid')}
              value={merchantDepositRequestDetails?.deposit_request?.uid}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.merchant-wallet-uid')}
              component={
                <MuiLink
                  underline="hover"
                  component={Link}
                  to={`${RouterPaths.Profiles}/${profileUid}/transactions`}
                  state={{
                    filterWalletUid:
                      merchantDepositRequestDetails?.deposit_request?.merchant_wallet_uid,
                  }}
                >
                  <Typography>
                    {merchantDepositRequestDetails?.deposit_request?.merchant_wallet_uid}
                  </Typography>
                </MuiLink>
              }
              value={merchantDepositRequestDetails?.deposit_request?.merchant_wallet_uid}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.external-id')}
              value={merchantDepositRequestDetails?.deposit_request?.ext_id}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.status')}
              value={merchantDepositRequestDetails?.deposit_request?.status}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.created-at')}
              value={UTCToDateTime(
                merchantDepositRequestDetails?.deposit_request?.created_at ?? ''
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.valid-to')}
              value={UTCToDateTime(merchantDepositRequestDetails?.deposit_request?.valid_to ?? '')}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.finished_at')}
              value={UTCToDateTime(
                merchantDepositRequestDetails?.deposit_request?.finished_at ?? ''
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.blockchain')}
              value={t(
                `common.blockchain.${merchantDepositRequestDetails?.deposit_request?.blockchain}`
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.deposit-coin')}
              value={merchantDepositRequestDetails?.deposit_request?.coin_code}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.deposit-address')}
              value={`${merchantDepositRequestDetails?.deposit_request?.deposit_address} ${t(
                'merchant-payments.details.head-cells.deposit-address-leased',
                {
                  leased_at: UTCToDateTime(
                    merchantDepositRequestDetails?.deposit_request?.leased_at
                  ),
                  ttl_seconds: merchantDepositRequestDetails?.deposit_request?.ttl_seconds,
                }
              )}`}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.request-amount')}
              value={undefined}
              formattedValue={formatBlockchainAmountWithCurrency(
                merchantDepositRequestDetails?.deposit_request?.req_amount,
                merchantDepositRequestDetails?.deposit_request?.req_coin_code
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.exchange-rate')}
              value={merchantDepositRequestDetails?.deposit_request?.exchange_rate}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.deposit-amount')}
              value={formatFiatAmountWithCurrency(
                merchantDepositRequestDetails?.deposit_request?.deposit_amount,
                merchantDepositRequestDetails?.deposit_request?.coin_code
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.paid-amount')}
              value={formatFiatAmountWithCurrency(
                merchantDepositRequestDetails?.deposit_request?.paid_amount,
                merchantDepositRequestDetails?.deposit_request?.coin_code
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.fee')}
              withoutDivider
              value={
                merchantDepositRequestDetails?.deposit_request?.fee &&
                formatFiatAmountWithCurrency(
                  merchantDepositRequestDetails?.deposit_request?.fee,
                  merchantDepositRequestDetails?.deposit_request?.coin_code
                )
              }
            />

            {/* <InfoField
              name={t('merchant-payments.details.head-cells.marked-as-sent-at')}
              value={UTCToDateTime(
                merchantDepositRequestDetails?.deposit_request?.marked_as_sent_at ?? ''
              )}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.ttl-seconds')}
              value={merchantDepositRequestDetails?.deposit_request?.ttl_seconds}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.info')}
              component={
                <ObjectToStrings data={merchantDepositRequestDetails?.deposit_request?.info} />
              }
              value={null}
              withEmptyValue
            /> */}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('merchant-payments.details.invoice-beneficiary-title')}
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('merchant-payments.details.head-cells.email')}
              value={merchantDepositRequestDetails?.deposit_request?.req_payer_info?.email}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.payer-type')}
              value={merchantDepositRequestDetails?.deposit_request?.req_payer_info?.payer_type}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.name')}
              value={
                merchantDepositRequestDetails?.deposit_request?.req_payer_info?.company_name ??
                `${merchantDepositRequestDetails?.deposit_request?.req_payer_info?.first_name} ${merchantDepositRequestDetails?.deposit_request?.req_payer_info?.last_name}`
              }
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.country')}
              value={
                merchantDepositRequestDetails?.deposit_request?.req_payer_info?.residential_address
                  .country_code
              }
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.city')}
              value={
                merchantDepositRequestDetails?.deposit_request?.req_payer_info?.residential_address
                  .city
              }
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.zip-code')}
              value={
                merchantDepositRequestDetails?.deposit_request?.req_payer_info?.residential_address
                  .zip_code
              }
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.address')}
              withoutDivider
              value={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <div>
                    {
                      merchantDepositRequestDetails?.deposit_request?.req_payer_info
                        ?.residential_address.address_line1
                    }
                  </div>
                  <div>
                    {merchantDepositRequestDetails?.deposit_request?.req_payer_info
                      ?.residential_address.address_line2 ?? undefined}
                  </div>
                </Box>
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('merchant-payments.details.payer-info-title')}
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('merchant-payments.details.head-cells.email')}
              value={merchantDepositRequestDetails?.deposit_request?.payer_email}
            />
            <InfoField
              name={t('merchant-payments.details.head-cells.type')}
              value={merchantDepositRequestDetails?.deposit_request?.payer_type}
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.name')}
              value={
                merchantDepositRequestDetails?.deposit_request?.payer_company_name ??
                `${merchantDepositRequestDetails?.deposit_request?.payer_first_name} ${merchantDepositRequestDetails?.deposit_request?.payer_last_name}`
              }
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.headers')}
              withoutDivider
              value={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.keys(
                    merchantDepositRequestDetails?.deposit_request?.payer_headers ?? {}
                  )?.map((header: any) => (
                    <Typography key={header}>
                      {header}:{' '}
                      {merchantDepositRequestDetails?.deposit_request?.payer_headers[header]}
                    </Typography>
                  ))}
                </Box>
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('merchant-payments.details.payer-info-title')}
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('merchant-payments.details.head-cells.return-url')}
              value={merchantDepositRequestDetails?.deposit_request?.config?.return_url}
              withEmptyValue
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.link-ttl-hours')}
              value={merchantDepositRequestDetails?.deposit_request?.config?.link_ttl_hours}
              withEmptyValue
            />

            <InfoField
              name={t('merchant-payments.details.head-cells.coins')}
              withoutDivider
              value={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography>
                    {coins?.coins
                      ?.filter((coin) =>
                        merchantDepositRequestDetails?.deposit_request?.config?.coin_ids?.includes(
                          coin.id
                        )
                      )
                      ?.map((coin) => coin.name)
                      .join(', ')}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('merchant-payments.details.related-transactions-title')}
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <Table headCells={headCells} rows={rows} isLoading={isFetching} />
            <TablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              total={transactions?.total ?? -1}
              totalPages={transactions?.total_pages ?? 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              isLoading={isFetching}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
